import { useEffect, useRef, useState } from 'react'
import { useGameStore } from '../../store/gameStore'
import { useGameActions } from '../../hooks/useGameActions'
import { useBeeBuzz } from '../../hooks/useAudio'
import { getAvailableActions, triggerBotTurns } from '../../api/game'
import { leaveRoom, requestReturnToLobby, setBotThinkingTime, transferMember } from '../../api/rooms'
import { assetUrl } from '../../utils/assets'
import { getLastModalTrigger } from '../../utils/modalEvents'
import type { Color, Flower, Season } from '../../types/game'

import { HiveBoard } from './HiveBoard'
import { FlowerMarket } from './FlowerMarket'
import { CurrentPlayerTableau } from './CurrentPlayerTableau'
import { OpponentsTableau } from './OpponentsTableau'
import { EvolutionTree } from './EvolutionTree'
import { TrackerBar } from './TrackerBar'
import { LogPanel } from './LogPanel'
import { WorkerFlightOverlay } from './WorkerFlightOverlay'
import { FlowerFlightOverlay } from './FlowerFlightOverlay'

import { QueenTradingModal } from '../modals/QueenTradingModal'
import { SeasonOverviewModal } from '../modals/SeasonOverviewModal'
import { ColonyCollapseEventOverviewModal } from '../modals/ColonyCollapseEventOverviewModal'
import { DonationModal } from '../modals/DonationModal'
import { DonationResponseModal } from '../modals/DonationResponseModal'
import { WoollyFurChoiceModal } from '../modals/WoollyFurChoiceModal'
import { WinterTradeModal } from '../modals/WinterTradeModal'
import { ColonyCollapseModal } from '../modals/ColonyCollapseModal'
import { GameEndModal } from '../modals/GameEndModal'
import { GameMenuModal } from '../modals/GameMenuModal'

interface GameShellProps {
  shellMode?: 'desktop' | 'mobile'
  boardZoom?: number
}

interface WorkerFlight {
  id: number
  color: Color
  startX: number
  startY: number
  deltaX: number
  deltaY: number
}

interface FlowerFlight {
  id: number
  flower: Flower
  currentSeason: Season
  startX: number
  startY: number
  deltaX: number
  deltaY: number
  width: number
  height: number
}

interface RectSnapshot {
  left: number
  top: number
  width: number
  height: number
}

interface PendingRemotePlacement {
  id: number
  color: Color
  action: string
  targetId: string | number | null
}

interface RemoteGameEvent {
  seq: number
  type: string
  data: {
    type?: string
    color?: string
    action?: string
    target?: { id?: string | number } | null
  }
}

function getPendingBotAutomationKey(room: NonNullable<ReturnType<typeof useGameStore.getState>['room']>): string | null {
  const game = room.game
  if (!game || game.pending_donation) {
    return null
  }
  if (game.state !== 'in_progress') {
    return null
  }

  if (game.phase === 'colony_collapse_offering') {
    const pendingBot = room.members.find((member) => {
      if (!member.is_bot || !member.color) {
        return false
      }
      const player = game.players.find((candidate) => candidate.color === member.color)
      return player !== undefined && Object.keys(player.offered_resources).length === 0
    })
    if (!pendingBot?.color) {
      return null
    }
    return `${room.id}:colony-collapse:${game.event_handler.current_event?.id ?? room.tip}:${pendingBot.color}:${room.tip}`
  }

  const currentMember = room.members.find((member) => member.color === game.current_player.color)
  if (!currentMember?.is_bot || !currentMember.color) {
    return null
  }

  return `${room.id}:turn:${game.current_player.color}:${room.tip}`
}

function getWorkerTargetSelector(event: RemoteGameEvent, myColor: Color | null): string | null {
  const color = event.data.color
  const action = event.data.action
  const targetId = event.data.target?.id

  if (!color || !action || color === myColor) {
    return null
  }

  switch (action) {
    case 'forage':
      return '[data-worker-target="forage-pool"]'
    case 'pollinate':
      return typeof targetId === 'number'
        ? `.opponent-overview[data-player-color="${color}"] [data-flower-id="${targetId}"]`
        : null
    case 'build_brood_chamber':
    case 'make_honey':
    case 'lay_egg':
    case 'clear_infection':
      return typeof targetId === 'string' ? `.board [data-cell-id="${targetId}"]` : null
    case 'evolve':
      return '[data-worker-target="evolve-pool"]'
    default:
      return null
  }
}

function getRemoteForageFlowerTargetSelector(event: RemoteGameEvent, myColor: Color | null): string | null {
  const color = event.data.color
  const action = event.data.action

  if (!color || !action || color === myColor || action !== 'forage') {
    return null
  }

  return `.opponent-overview[data-player-color="${color}"] .opponent-flowers`
}

export function GameShell({ shellMode = 'desktop', boardZoom = 1 }: GameShellProps) {
  const playBeeBuzz = useBeeBuzz()
  const [workerFlights, setWorkerFlights] = useState<WorkerFlight[]>([])
  const [flowerFlights, setFlowerFlights] = useState<FlowerFlight[]>([])
  const [pendingRemotePlacements, setPendingRemotePlacements] = useState<PendingRemotePlacement[]>([])
  const flightIdRef = useRef(0)
  const flightTimeoutsRef = useRef<number[]>([])
  const botAutomationKeyRef = useRef<string | null>(null)
  const processedEventSeqRef = useRef(0)
  const meadowFlowerRectsRef = useRef<Map<number, RectSnapshot>>(new Map())
  const {
    room,
    member,
    memberId,
    availableActions,
    events,
    showQueenTrading,
    showSeasonOverview,
    showDonationModal,
    showDonationResponse,
    showColonyCollapse,
    showColonyCollapseOverview,
    showGameEnd,
    showGameMenu,
    setAvailableActions,
    setShowQueenTrading,
    setShowSeasonOverview,
    setShowDonationModal,
    setShowDonationResponse,
    setShowColonyCollapse,
    setShowColonyCollapseOverview,
    setShowGameEnd,
    setShowGameMenu,
    setPendingSeasonOverview,
    setDismissedSeasonOverviewSeq,
    setDismissedColonyCollapseOverviewSeq,
    setAnimationBlockUntil,
    setRoom,
    setRoomId,
    setScreen,
  } = useGameStore()

  const {
    forage,
    pollinate,
    buildBroodChamber,
    makeHoney,
    layEgg,
    clearInfection,
    evolve,
    pass,
    tradeWithQueen,
    finishQueenTrading,
    donate,
    acceptDonation,
    rejectDonation,
    resolveWoollyFur,
    winterTrade,
    submitColonyCollapseOffer,
    resolveColonyCollapse,
  } = useGameActions()

  const game = room?.game
  const myColor = member?.color ?? null
  const myPlayer = game?.players.find((p) => p.color === myColor) ?? null
  const seasonOverviewTrigger = getLastModalTrigger(room, 'season-overview', myColor)
  const colonyCollapseOverviewTrigger = getLastModalTrigger(room, 'colony-collapse-overview', myColor)
  const isActionAvailable = (action: string, targetId?: string | number) =>
    availableActions.some((option) => option.action === action && option.target_id === (targetId ?? null))

  const renderedGame = (() => {
    if (!game || pendingRemotePlacements.length === 0) {
      return game
    }

    let nextGame = game

    for (const placement of pendingRemotePlacements) {
      switch (placement.action) {
        case 'forage': {
          const index = [...nextGame.foragers].lastIndexOf(placement.color)
          if (index >= 0) {
            const foragers = [...nextGame.foragers]
            foragers.splice(index, 1)
            nextGame = { ...nextGame, foragers }
          }
          break
        }
        case 'build_brood_chamber':
        case 'make_honey':
        case 'lay_egg':
        case 'clear_infection': {
          if (typeof placement.targetId === 'string' && nextGame.hive_workers[placement.targetId] === placement.color) {
            const hiveWorkers = { ...nextGame.hive_workers }
            delete hiveWorkers[placement.targetId]
            nextGame = { ...nextGame, hive_workers: hiveWorkers }
          }
          break
        }
        case 'pollinate': {
          if (typeof placement.targetId === 'number') {
            nextGame = {
              ...nextGame,
              players: nextGame.players.map((player) =>
                player.color === placement.color
                  ? {
                      ...player,
                      pollinated_flowers: player.pollinated_flowers.filter((flower) => flower.id !== placement.targetId),
                    }
                  : player
              ),
            }
          }
          break
        }
        case 'evolve': {
          if (typeof placement.targetId === 'number' && nextGame.evolution_tracker[placement.color] === placement.targetId) {
            const evolutionTracker = { ...nextGame.evolution_tracker }
            delete evolutionTracker[placement.color]
            nextGame = { ...nextGame, evolution_tracker: evolutionTracker }
          }
          break
        }
      }
    }

    return nextGame
  })()
  const renderedMyPlayer = renderedGame?.players.find((p) => p.color === myColor) ?? null

  useEffect(() => {
    return () => {
      for (const timeoutId of flightTimeoutsRef.current) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [])

  const queueWorkerFlight = (color: Color, target: Element | null) => {
    const source =
      document.querySelector(`[data-worker-source="player-${color}"] .worker`) ??
      document.querySelector(`[data-worker-source="player-${color}"]`)

    console.debug('[worker-animation]', {
      color,
      foundSourceElement: Boolean(source),
      foundTargetElement: Boolean(target),
      targetClassName: target instanceof Element ? target.className : null,
    })

    if (!target) {
      console.debug('[worker-animation] skipped: target element not found', { color })
      return
    }
    if (!source) {
      console.debug('[worker-animation] skipped: source element not found', { color })
      return
    }

    const sourceRect = source.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()
    const id = flightIdRef.current++
    const startX = sourceRect.left + sourceRect.width / 2
    const startY = sourceRect.top + sourceRect.height / 2
    const endX = targetRect.left + targetRect.width / 2
    const endY = targetRect.top + targetRect.height / 2
    console.debug('[worker-animation] geometry', {
      color,
      id,
      sourceRect: {
        left: sourceRect.left,
        top: sourceRect.top,
        width: sourceRect.width,
        height: sourceRect.height,
      },
      targetRect: {
        left: targetRect.left,
        top: targetRect.top,
        width: targetRect.width,
        height: targetRect.height,
      },
      startX,
      startY,
      endX,
      endY,
      deltaX: endX - startX,
      deltaY: endY - startY,
    })
    setAnimationBlockUntil(Date.now() + 800)
    playBeeBuzz(780)

    setWorkerFlights((current) => [
      ...current,
      {
        id,
        color,
        startX,
        startY,
        deltaX: endX - startX,
        deltaY: endY - startY,
      },
    ])

    const timeoutId = window.setTimeout(() => {
      setWorkerFlights((current) => current.filter((flight) => flight.id !== id))
      flightTimeoutsRef.current = flightTimeoutsRef.current.filter((knownId) => knownId !== timeoutId)
    }, 780)
    flightTimeoutsRef.current.push(timeoutId)
  }

  useEffect(() => {
    if (workerFlights.length === 0) {
      return
    }
    console.debug('[worker-animation] overlay state', workerFlights)
  }, [workerFlights])

  const animateToSelector = (color: Color, selector: string) => {
    queueWorkerFlight(color, document.querySelector(selector))
  }

  const queueFlowerFlight = (
    flower: Flower,
    source: Element | null,
    target: Element | null,
    sourceRectOverride?: RectSnapshot | null
  ) => {
    if (!target || !game) {
      return
    }

    const sourceRect = source
      ? source.getBoundingClientRect()
      : sourceRectOverride
    if (!sourceRect) {
      return
    }
    const targetRect = target.getBoundingClientRect()
    const id = flightIdRef.current++
    const startX = sourceRect.left
    const startY = sourceRect.top
    const endX = targetRect.left + (targetRect.width - sourceRect.width) / 2
    const endY = targetRect.top + (targetRect.height - sourceRect.height) / 2

    setFlowerFlights((current) => [
      ...current,
      {
        id,
        flower,
        currentSeason: game.current_season,
        startX,
        startY,
        deltaX: endX - startX,
        deltaY: endY - startY,
        width: sourceRect.width,
        height: sourceRect.height,
      },
    ])

    const timeoutId = window.setTimeout(() => {
      setFlowerFlights((current) => current.filter((flight) => flight.id !== id))
      flightTimeoutsRef.current = flightTimeoutsRef.current.filter((knownId) => knownId !== timeoutId)
    }, 780)
    flightTimeoutsRef.current.push(timeoutId)
  }

  const animateForagedFlower = (flowerId: number) => {
    if (!game) {
      return
    }

    const flower = game.meadow.flowers.find((candidate) => candidate.id === flowerId)
    if (!flower) {
      return
    }

    const source = document.querySelector(`.flower-market [data-flower-id="${flowerId}"]`)
    const target =
      document.querySelector('.current-player-tableau .player-flowers .flower-placeholder') ??
      document.querySelector('.current-player-tableau .player-flowers')

    queueFlowerFlight(flower, source, target)
  }

  const animateRemoteToSelector = (color: Color, selector: string) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        queueWorkerFlight(color, document.querySelector(selector))
      })
    })
  }

  const queueRemotePlacementMask = (color: Color, action: string, targetId: string | number | null) => {
    const id = flightIdRef.current++
    setPendingRemotePlacements((current) => [...current, { id, color, action, targetId }])
    const timeoutId = window.setTimeout(() => {
      setPendingRemotePlacements((current) => current.filter((placement) => placement.id !== id))
      flightTimeoutsRef.current = flightTimeoutsRef.current.filter((knownId) => knownId !== timeoutId)
    }, 780)
    flightTimeoutsRef.current.push(timeoutId)
  }

  const animateRemoteForagedFlower = (flowerId: number, color: Color) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const flower = game?.meadow.flowers.find((candidate) => candidate.id === flowerId)
        const source = document.querySelector(`.flower-market [data-flower-id="${flowerId}"]`)
        const target = document.querySelector(`.opponent-overview[data-player-color="${color}"] .opponent-flowers`)
        const cachedSourceRect = meadowFlowerRectsRef.current.get(flowerId) ?? null

        console.debug('[forage-animation][remote]', {
          flowerId,
          color,
          roomTip: room?.tip ?? null,
          marketFlowerIds: game?.flower_market.map((candidate) => candidate.id) ?? [],
          foundFlowerInMeadow: Boolean(flower),
          foundSourceElement: Boolean(source),
          foundCachedSourceRect: Boolean(cachedSourceRect),
          foundTargetElement: Boolean(target),
          eventCount: events.length,
        })

        if (!flower) {
          console.debug('[forage-animation][remote] skipped: flower not found in meadow catalog', {
            flowerId,
            color,
          })
          return
        }
        queueFlowerFlight(flower, source, target, cachedSourceRect)
      })
    })
  }

  useEffect(() => {
    if (shellMode !== 'desktop' || !game) {
      meadowFlowerRectsRef.current.clear()
      return
    }

    const snapshotId = window.requestAnimationFrame(() => {
      const nextRects = new Map<number, RectSnapshot>()
      for (const flower of game.flower_market) {
        const element = document.querySelector(`.flower-market [data-flower-id="${flower.id}"]`)
        if (!element) {
          continue
        }
        const rect = element.getBoundingClientRect()
        nextRects.set(flower.id, {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        })
      }
      meadowFlowerRectsRef.current = nextRects
    })

    return () => window.cancelAnimationFrame(snapshotId)
  }, [game?.flower_market, room?.tip, shellMode])

  // Fetch available actions periodically and when game state changes
  useEffect(() => {
    if (!room || !game || !memberId) return

    const fetchActions = async () => {
      try {
        const response = await getAvailableActions(room.id, memberId)
        setAvailableActions(response.actions)
      } catch {
        setAvailableActions([])
      }
    }

    fetchActions()
  }, [room?.tip, memberId])

  const handleLeave = async () => {
    if (!room) return
    try {
      await leaveRoom(room.id, memberId)
      setRoomId(null)
      setRoom(null)
      setScreen('menu')
    } catch {
      setRoomId(null)
      setRoom(null)
      setScreen('menu')
    }
  }

  const handleReturnToLobby = () => {
    if (!room) return
    // broadcasts a room event; every client (including this one) transfers
    // itself back to the lobby when it sees it
    void requestReturnToLobby(room.id).catch(() => {})
  }

  // the host's remembered thinking time (persisted like the audio settings)
  // is applied once per game room, so it survives across sessions
  const appliedThinkingTimeRoomRef = useRef<string | null>(null)
  useEffect(() => {
    if (!room?.id || room.is_lobby || !game) return
    if (appliedThinkingTimeRoomRef.current === room.id) return
    const me = room.members.find((member) => member.id === memberId)
    if (!me?.is_host) return
    appliedThinkingTimeRoomRef.current = room.id
    const remembered = useGameStore.getState().botThinkingTime
    if (Math.abs((room.bot_thinking_time ?? 3) - remembered) < 0.01) return
    void setBotThinkingTime(room.id, remembered).catch(() => {})
  }, [room?.id, room?.is_lobby, game, memberId])

  const returnToLobbySeqRef = useRef(0)
  useEffect(() => {
    if (!room?.id || room.is_lobby) return
    const signal = events.find(
      (event) =>
        event.type === 'room' &&
        (event.data as { payload?: { action?: string } }).payload?.action === 'return_to_lobby' &&
        event.seq > returnToLobbySeqRef.current
    )
    if (!signal) return
    returnToLobbySeqRef.current = signal.seq
    void (async () => {
      try {
        const result = await transferMember({
          member_id: memberId,
          source_room_id: room.id,
          target_room_id: '000-000-000',
          delete_empty_source: true,
        })
        setShowGameEnd(false)
        setRoomId(result.room_id)
      } catch {
        setShowGameEnd(false)
        setRoomId(null)
        setRoom(null)
        setScreen('menu')
      }
    })()
  }, [events, room?.id, room?.is_lobby, memberId])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && room && game && !room.is_lobby) {
        setShowGameMenu(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [game, room])

  useEffect(() => {
    if (!room || !game) {
      botAutomationKeyRef.current = null
      return
    }

    const automationKey = getPendingBotAutomationKey(room)
    if (!automationKey) {
      botAutomationKeyRef.current = null
      return
    }
    if (botAutomationKeyRef.current === automationKey) {
      return
    }

    botAutomationKeyRef.current = automationKey
    triggerBotTurns(room.id).catch(() => {
      if (botAutomationKeyRef.current === automationKey) {
        botAutomationKeyRef.current = null
      }
    })
  }, [game, room])

  useEffect(() => {
    if (!room?.id) {
      processedEventSeqRef.current = 0
      return
    }

    processedEventSeqRef.current = events.reduce(
      (latestSeq, event) => Math.max(latestSeq, event.seq),
      processedEventSeqRef.current
    )
  }, [room?.id])

  useEffect(() => {
    if (shellMode !== 'desktop' || !room?.id || !game) {
      return
    }

    const pendingEvents = events.filter((event) => event.seq > processedEventSeqRef.current)
    if (pendingEvents.length === 0) {
      return
    }

    for (const event of pendingEvents as RemoteGameEvent[]) {
      console.debug('[remote-animation] inspecting event', {
        seq: event.seq,
        type: event.type,
        color: event.data.color ?? null,
        action: event.data.action ?? null,
        targetId: event.data.target?.id ?? null,
        myColor,
      })

      if (event.type !== 'game' || !event.data.color) {
        continue
      }

      const targetSelector = getWorkerTargetSelector(event, myColor)
      if (!targetSelector) {
        const remoteForageTarget = getRemoteForageFlowerTargetSelector(event, myColor)
        if (
          remoteForageTarget &&
          event.data.color &&
          typeof event.data.target?.id === 'number'
        ) {
          console.debug('[forage-animation][remote] received remote forage event without worker target selector', {
            seq: event.seq,
            color: event.data.color,
            targetId: event.data.target.id,
            action: event.data.action,
          })
          animateRemoteForagedFlower(event.data.target.id, event.data.color as Color)
        }
        continue
      }

      console.debug('[worker-animation][remote] queueing', {
        seq: event.seq,
        color: event.data.color,
        action: event.data.action,
        targetSelector,
      })
      queueRemotePlacementMask(
        event.data.color as Color,
        event.data.action ?? '',
        event.data.target?.id ?? null
      )
      animateRemoteToSelector(event.data.color as Color, targetSelector)
      if (
        event.data.action === 'forage' &&
        typeof event.data.target?.id === 'number'
      ) {
        console.debug('[forage-animation][remote] processing remote forage event', {
          seq: event.seq,
          color: event.data.color,
          targetId: event.data.target.id,
          targetSelector,
        })
        animateRemoteForagedFlower(event.data.target.id, event.data.color as Color)
      }
    }

    processedEventSeqRef.current = pendingEvents[pendingEvents.length - 1].seq
  }, [events, game, myColor, room?.id, shellMode])

  if (!renderedGame || !room) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'floralwhite',
        fontSize: 'var(--fs-xl)',
      }}>
        Loading game...
      </div>
    )
  }

  const isDesktop = shellMode === 'desktop'

  return (
    <div
      id={isDesktop ? 'desktop-shell' : 'mobile-game-shell'}
      className={`game-shell shell ${isDesktop ? 'desktop-shell' : 'mobile-game-shell'}`}
    >
      <img
        src={assetUrl('images', 'tabletop3.jpg')}
        alt=""
        className="tabletop"
      />
      {isDesktop ? <WorkerFlightOverlay flights={workerFlights} /> : null}
      {isDesktop ? <FlowerFlightOverlay flights={flowerFlights} /> : null}
      {isDesktop ? <TrackerBar game={renderedGame} /> : null}

        <HiveBoard
          game={renderedGame}
          seeds={room.seeds}
          availableActions={availableActions}
          isMyTurn={myColor !== null && renderedGame.current_player.color === myColor}
          draggable={!isDesktop}
          onBuild={(cellId) => {
            if (myColor && isActionAvailable('build_brood_chamber', cellId)) {
              animateToSelector(myColor, `.board [data-cell-id="${cellId}"]`)
            }
            void buildBroodChamber(cellId)
          }}
          onMakeHoney={(cellId) => {
            if (myColor && isActionAvailable('make_honey', cellId)) {
              animateToSelector(myColor, `.board [data-cell-id="${cellId}"]`)
            }
            void makeHoney(cellId)
          }}
          onLayEgg={(cellId) => {
            if (myColor && isActionAvailable('lay_egg', cellId)) {
              animateToSelector(myColor, `.board [data-cell-id="${cellId}"]`)
            }
            void layEgg(cellId)
          }}
          onClearInfection={(cellId) => {
            if (myColor && isActionAvailable('clear_infection', cellId)) {
              animateToSelector(myColor, `.board [data-cell-id="${cellId}"]`)
            }
            void clearInfection(cellId)
          }}
        scale={boardZoom}
      />

      {isDesktop ? (
        <FlowerMarket
          game={renderedGame}
          availableActions={availableActions}
          onForage={(flowerId) => {
            if (myColor && isActionAvailable('forage', flowerId)) {
              animateToSelector(myColor, '[data-worker-target="forage-pool"]')
              animateForagedFlower(flowerId)
            }
            void forage(flowerId)
          }}
        />
      ) : null}

      {isDesktop && renderedMyPlayer ? (
        <CurrentPlayerTableau
          game={renderedGame}
          player={renderedMyPlayer}
          member={member}
          availableActions={availableActions}
          animatedWorkerDelta={workerFlights.filter((flight) => flight.color === myColor).length}
          onPass={pass}
          onPollinate={(flowerId) => {
            if (myColor && isActionAvailable('pollinate', flowerId)) {
              animateToSelector(myColor, `.current-player-tableau [data-flower-id="${flowerId}"]`)
            }
            void pollinate(flowerId)
          }}
          onOpenDonate={() => setShowDonationModal(true)}
        />
      ) : null}

      {isDesktop ? (
        <OpponentsTableau
          game={renderedGame}
          myPlayerColor={myColor}
          members={room.members}
        />
      ) : null}

      {isDesktop ? (
        <EvolutionTree
          game={renderedGame}
          availableActions={availableActions}
          onEvolve={(evolutionId) => {
            if (myColor && isActionAvailable('evolve', evolutionId)) {
              animateToSelector(myColor, '[data-worker-target="evolve-pool"]')
            }
            void evolve(evolutionId)
          }}
        />
      ) : null}

      {isDesktop ? (
        <LogPanel
          game={renderedGame}
          room={room}
          memberId={memberId}
          onOpenMenu={() => setShowGameMenu(true)}
        />
      ) : null}

      {/* Modals */}
      {showQueenTrading && renderedGame.phase === 'queen_trading' && (
        <QueenTradingModal
          game={renderedGame}
          onTrade={async (tradeId) => {
            await tradeWithQueen(tradeId)
          }}
          onFinish={async () => {
            await finishQueenTrading()
            setShowQueenTrading(false)
          }}
        />
      )}

      {showSeasonOverview && (
        <SeasonOverviewModal
          game={renderedGame}
          members={room.members}
          onClose={() => {
            setDismissedSeasonOverviewSeq(seasonOverviewTrigger?.seq ?? null)
            setShowSeasonOverview(false)
            setPendingSeasonOverview(false)
          }}
        />
      )}

      {showColonyCollapseOverview && renderedGame?.colony_collapse_result && (
        <ColonyCollapseEventOverviewModal
          game={renderedGame}
          members={room.members}
          onClose={() => {
            setDismissedColonyCollapseOverviewSeq(colonyCollapseOverviewTrigger?.seq ?? null)
            setShowColonyCollapseOverview(false)
          }}
        />
      )}

      {showDonationModal && renderedMyPlayer && (
        <DonationModal
          game={renderedGame}
          members={room.members}
          leftMembers={room.left_members}
          memberId={memberId}
          onDonate={donate}
          onClose={() => setShowDonationModal(false)}
        />
      )}

      {showDonationResponse && renderedGame.pending_donation && (
        <DonationResponseModal
          game={renderedGame}
          members={room.members}
          onAccept={async () => {
            await acceptDonation()
            setShowDonationResponse(false)
          }}
          onReject={async () => {
            await rejectDonation()
            setShowDonationResponse(false)
          }}
        />
      )}

      {renderedGame.phase === 'woolly_fur_choice' &&
        renderedGame.pending_woolly_fur_choice &&
        renderedGame.pending_woolly_fur_choice.player === myColor && (
          <WoollyFurChoiceModal
            game={renderedGame}
            onChoose={(resource) => {
              void resolveWoollyFur(resource)
            }}
          />
        )}

      {renderedGame.phase === 'winter_trading' &&
        myColor &&
        renderedGame.pending_winter_traders?.includes(myColor as never) && (
          <WinterTradeModal
            game={renderedGame}
            myColor={myColor}
            onTrade={(nPropolis) => {
              void winterTrade(nPropolis)
            }}
          />
        )}

      {showColonyCollapse && renderedGame.event_handler.current_event && (
        <ColonyCollapseModal
          game={renderedGame}
          player={renderedMyPlayer ?? renderedGame.current_player}
          members={room.members}
          onSubmitOffer={async (offers) => {
            await submitColonyCollapseOffer(offers)
          }}
          onResolve={async (force = false) => {
            await resolveColonyCollapse(force)
          }}
        />
      )}

      {showGameEnd && (
        <GameEndModal
          game={renderedGame}
          members={room.members}
          myColor={myColor}
          onLeave={handleLeave}
          onReturnToLobby={handleReturnToLobby}
        />
      )}

      {showGameMenu && (
        <GameMenuModal
          onBotThinkingTimeChange={(seconds) => {
            void setBotThinkingTime(room.id, seconds).catch(() => {})
          }}
          onClose={() => setShowGameMenu(false)}
          onLeave={async () => {
            setShowGameMenu(false)
            await handleLeave()
          }}
        />
      )}
    </div>
  )
}
